// assetFolderPath — the one place that knows how an asset's folder is read
// from, and written to, the server.
//
// This module exists because the same three questions were answered four times
// (drag-drop onto a folder, the multi-select Move-to picker, folder rename, and
// the shadow migration) by four hand-written if/else-if chains, and every one
// of them answered at least one of the three wrong:
//
//   1. "What folder is this asset in?" — every reader supplied its own
//      hardcoded fallback (`row.folder_path ?? '/materials'`). That fallback
//      hid a real defect for months: the API emitted the folder only as
//      `folderPath`, the client read `folder_path`, so the fallback answered
//      for EVERY row and a server-side folder was never displayed at all.
//      There is no fallback here. A row that states no folder returns null,
//      which the caller must surface — absence is data, not a location.
//
//   2. "Which endpoint moves this kind?" — an if/else-if chain that names kinds
//      silently does nothing for a kind nobody added, which is how folder
//      rename quietly left environment assets behind. folderUpdaterFor throws.
//
//   3. "Did the write land?" — a 200 is not proof. A PATCH whose folder field
//      bound to nothing returns 200 and the unchanged row, and the client then
//      deleted its own localStorage record of the move on the strength of that
//      200. verifyFolderWrite compares what the server echoed against what was
//      asked for, so a dropped field is loud instead of being data loss.
//
// Pure: no CustomAssetManager import, no React, no localStorage. The updater
// table takes `cam` as an argument so a unit test can drive it with a fake.

// Kinds whose folder lives in a server column. `rig` and `ogs` records are
// synthesized client-side and have no folder row.
const SERVER_FOLDER_KINDS = new Set([
  'material', 'texture', 'body_anim', 'face_anim', 'audio', 'prefab', 'environment',
  // ENG-1030's blueprint library. Registered here rather than in ENG-1030 so the
  // two changes meet in ONE place: that branch was adding this kind by extending
  // the three if/else-if chains this module replaced, which would have merged
  // clean and then thrown 'no folder updater for asset kind "blueprint_asset"'
  // on the first move. On a checkout without ENG-1030 no such record exists
  // (CustomAssetManager has no listBlueprintAssets), so nothing reaches the
  // updater below.
  'blueprint_asset',
])

// True when this record maps to a server row that owns a folder_path.
//
// A legacy per-slot texture is keyed by its slot ("body.diffuse"), not a UUID:
// it is not a row in texture_assets, so it has no folder to move and a missing
// folder_path on it is expected rather than a defect.
export function hasServerFolder(record) {
  if (!record || !SERVER_FOLDER_KINDS.has(record.kind)) return false
  if (record.kind === 'texture' && String(record.rawId ?? '').includes('.')) return false
  return true
}

// A server row's folder, with NO fallback. Returns null when the row states
// none — the caller surfaces that; it does not invent a location.
export function serverFolderOf(row) {
  const v = row?.folder_path
  return typeof v === 'string' ? v : null
}

// Folder path as the API spells it: leading slash, no trailing slash, root is
// "/". `['props','hats']` → "/props/hats"; `[]` → "/".
export function folderPathFromSegments(segments) {
  const parts = (Array.isArray(segments) ? segments : []).filter(Boolean)
  return parts.length ? '/' + parts.join('/') : '/'
}

// Path segments from an API folder string. "" and "/" both mean root.
export function segmentsFromFolderPath(path) {
  return typeof path === 'string' ? path.split('/').filter(Boolean) : []
}

// One spelling of a folder for comparison. The root is reachable as "" (the
// pre-migration column default) and as "/" (what a move-to-root sends), and
// treating those as different made every root move report a failed write.
export function normalizeFolder(path) {
  if (typeof path !== 'string') return null
  const trimmed = path.replace(/\/+$/, '')
  return trimmed === '' ? '/' : trimmed
}

// kind → the CustomAssetManager call that moves it. Every kind in
// SERVER_FOLDER_KINDS must appear here; folderUpdaterFor asserts it.
const FOLDER_UPDATERS = {
  material:    (cam, id, body) => cam.updateMaterial(id, body),
  texture:     (cam, id, body) => cam.updateTextureAsset(id, body),
  body_anim:   (cam, id, body) => cam.updateAnimationAsset(id, body),
  face_anim:   (cam, id, body) => cam.updateAnimationAsset(id, body),
  audio:       (cam, id, body) => cam.updateAudioAsset(id, body),
  prefab:      (cam, id, body) => cam.updatePrefab(id, body),
  environment: (cam, id, body) => cam.updateEnvironmentAsset(id, body),
  // Snake case like every other kind: /api/blueprint-assets/{id} accepts BOTH
  // casings (avatar-asset-manager 660b19b added the alias for exactly the reason
  // this module exists), so the uniform body needs no special case here.
  blueprint_asset: (cam, id, body) => cam.updateBlueprintAsset(id, body),
}

// Throws rather than returning undefined: a caller that silently skips an
// unknown kind reports a successful move that never happened, which is the
// exact failure this module exists to make impossible.
export function folderUpdaterFor(kind) {
  const fn = FOLDER_UPDATERS[kind]
  if (!fn) throw new Error(`assetFolderPath: no folder updater for asset kind "${String(kind)}"`)
  return fn
}

// Kinds the updater table covers — exported so a test can assert it matches
// SERVER_FOLDER_KINDS exactly, which is what keeps a newly added foldered kind
// from silently having no way to be moved.
export function updatableFolderKinds() {
  return Object.keys(FOLDER_UPDATERS).sort()
}

export function serverFolderKinds() {
  return [...SERVER_FOLDER_KINDS].sort()
}

// Did the move land? `row` is the server's echo of the row it wrote.
//
// Three outcomes, and the middle one is the bug that motivated this module:
//   ok           — the row reports the folder we asked for.
//   not-reported — the row states no folder_path at all, so the write cannot be
//                  confirmed. Before the API dual-emitted folder_path this was
//                  the response for every material and texture.
//   mismatch     — the row reports a different folder: the field was dropped
//                  (the pre-fix camelCase-only binding) or another writer won.
export function verifyFolderWrite(requestedPath, row) {
  const reported = serverFolderOf(row)
  if (reported === null) {
    return {
      ok: false,
      reason: 'not-reported',
      reported: null,
      message: `the server accepted the move but its response states no folder_path, so the write cannot be confirmed`,
    }
  }
  if (normalizeFolder(reported) !== normalizeFolder(requestedPath)) {
    return {
      ok: false,
      reason: 'mismatch',
      reported,
      message: `the server accepted the move but reports the asset in "${reported}", not "${requestedPath}"`,
    }
  }
  return { ok: true, reason: null, reported }
}
